import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { Alert, Table, Tag, Select, Button, Dropdown, Modal, message } from 'antd';
import { MoreOutlined, UserAddOutlined, TagsOutlined, MailOutlined, LinkOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import AppRangePicker from '@/components/common/AppRangePicker';
import PermissionGate from '../../components/auth/PermissionGate';
import ifaService from '../../services/ifaService';
import orderService from '../../services/orderService';
import apiClient from '../../api/apiClient';
import { buildRaReferralLink } from '../../api/baseUrls';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import AssignPricingGroupDrawer from '../../components/ifa/AssignPricingGroupDrawer';
import AssignRmDrawer from '../../components/ifa/AssignRmDrawer';
import PartnerInviteOverlay from '../../components/ifa/PartnerInviteOverlay';
import RmReferralLinkButton from '../../components/ifa/RmReferralLinkButton';
import { useDashboardDrilldownFilters } from '../../helpers/dashboardDrilldown.helpers';

const { Option } = Select;

const DEFAULT_SORT = { sort_by: 'created_at', sort_order: 'desc' };
const DEFAULT_PAGE_SIZE = 15;

function columnSortOrder(sortState, field) {
  if (sortState.sort_by !== field) return null;
  return sortState.sort_order === 'asc' ? 'ascend' : 'descend';
}

const ALREADY_APPROVED_COPY =
  'Status is already complete. This partner onboarded themselves as an investor — Change status is not needed.';

function getPartnerUuid(record) {
  return record?.id ?? record?.uuid ?? '';
}

function getPartnerListColumns({
  sortState,
  canAssignRm,
  canAssignPricingGroup,
  canChangePartnerStatus,
  goToPartnerDetails,
  handleResendPasswordSetup,
  handleCopyRaReferralLink,
  onAssignRm,
  onAssignPricingGroup,
  onChangeStatus,
}) {
  return [
    {
      title: 'PARTNER ID',
      key: 'id',
      render: (_, record) => (
        <span className="font-mono text-purple-600 font-medium px-2 py-0.5 bg-purple-50 rounded">
          {record.display_id ?? '—'}
        </span>
      ),
    },
    {
      title: 'PARTNER NAME',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'name'),
      render: (text, record) => (
        <span
          className="font-semibold text-blue-700 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            goToPartnerDetails(record);
          }}
        >
          {text || 'N/A'}
        </span>
      ),
    },
    {
      title: 'RM NAME',
      dataIndex: 'manager_name',
      key: 'manager_name',
      render: (text) => {
        const name = text || 'Not Assigned';
        const isNotAssigned = name === 'Not Assigned';
        return <span className={`font-medium ${isNotAssigned ? 'text-gray-400 italic' : 'text-green-700'}`}>{name}</span>;
      },
    },
    {
      title: 'MOBILE',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (text) => <span className="text-gray-700 tracking-wide">{text || '—'}</span>,
    },
    {
      title: 'PAN',
      dataIndex: 'pan',
      key: 'pan',
      render: (text) => (
        text ? <span className="font-mono text-blue-600 font-medium px-2 py-0.5 bg-blue-50 border border-blue-200 rounded">{text}</span> : <span className="text-gray-400">—</span>
      ),
    },
    {
      title: 'ACCOUNT STATUS',
      key: 'account_status',
      render: (_, record) => {
        const isActive = record.is_active;
        return (
          <Tag color={isActive ? 'green' : 'red'} className="m-0 border-0 font-medium px-2 rounded">
            {isActive ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'KYC STATUS',
      key: 'kyc_status',
      render: (_, record) => {
        const onboardingStatus = record.onboarding_status;

        let subText = 'Pending';
        let subColor = 'orange';

        if (onboardingStatus === 'APPROVED' || onboardingStatus === 'COMPLETED') {
          subText = 'Completed';
          subColor = 'green';
        } else if (onboardingStatus === 'IN_REVIEW') {
          subText = 'In Review';
          subColor = 'blue';
        }

        return (
          <Tag color={subColor} className="m-0 border border-gray-200 px-2 rounded font-medium">
            {subText}
          </Tag>
        );
      },
    },
    {
      title: 'AGREEMENT',
      key: 'agreement_status',
      render: (_, record) => {
        const status = (record.agreement_status || 'REQUESTED').toUpperCase();
        const isCompleted = status === 'COMPLETED' || status === 'SIGNED';
        return (
          <Tag color={isCompleted ? 'green' : 'orange'} className="border border-gray-200 rounded px-2">
            {isCompleted ? 'Completed' : 'Requested'}
          </Tag>
        );
      },
    },
    {
      title: 'PRICING GROUP',
      key: 'pricing_group',
      render: (_, record) =>
        record.pricing_group_name ? (
          <Tag color="blue" className="m-0 border-0 font-medium px-2 rounded">
            {record.pricing_group_name}
          </Tag>
        ) : (
          <span className="text-gray-400 italic">Not assigned</span>
        ),
    },
    {
      title: 'CREATED ON',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'created_at'),
      render: (date) => <span className="text-sm text-gray-600 font-medium">{date ? dayjs(date).format('DD-MM-YYYY') : '—'}</span>,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        const isNotAssigned = !record.manager_name || record.manager_name === 'Not Assigned';

        const items = [];

        if (canAssignRm) {
          items.push({
            key: 'assign-rm',
            icon: <UserAddOutlined />,
            label: isNotAssigned ? 'Assign RM' : 'Change RM',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              onAssignRm(record);
            },
          });
        }

        if (canAssignPricingGroup) {
          items.push({
            key: 'assign-pricing-group',
            icon: <TagsOutlined />,
            label: record.pricing_group_name ? 'Change pricing group' : 'Assign pricing group',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              onAssignPricingGroup(record);
            },
          });
        }

        if (canChangePartnerStatus) {
          items.push({
            key: 'change-status',
            icon: <CheckCircleOutlined />,
            label: 'Change status',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              onChangeStatus(record);
            },
          });
        }

        items.push({
          key: 'resend-password-setup',
          icon: <MailOutlined />,
          label: 'Re-send password setup link',
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            handleResendPasswordSetup(record);
          },
        });

        items.push({
          key: 'copy-ra-referral-link',
          icon: <LinkOutlined />,
          label: 'Copy RA referral link',
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            handleCopyRaReferralLink(record);
          },
        });

        return (
          <Dropdown
            menu={{ items, onClick: ({ domEvent }) => domEvent.stopPropagation() }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-600 hover:text-gray-900"
            />
          </Dropdown>
        );
      },
    },
  ];
}

function parseDateRangeFromParams(searchParams) {
  const from = searchParams.get('date_from');
  const to = searchParams.get('date_to');
  return from && to ? [dayjs(from), dayjs(to)] : null;
}

function buildFiltersFromUrl(searchParams, drilldown) {
  const activeInPeriod = searchParams.get('active_in_period') === '1';
  let filters = {
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'all',
    start_date: '',
    end_date: '',
    manager_id: searchParams.get('rm') || '',
    agreement_status: searchParams.get('agreement') || '',
    bank_approval_status: searchParams.get('bank_approval_status') || '',
    active_in_period: activeInPeriod,
  };

  if (drilldown.dateRange) {
    if (drilldown.activeInPeriod) {
      return {
        ...filters,
        start_date: drilldown.apiParams.start_date,
        end_date: drilldown.apiParams.end_date,
        active_in_period: true,
      };
    }
    return { ...filters, ...drilldown.filterDates };
  }

  const range = parseDateRangeFromParams(searchParams);
  if (range) {
    if (activeInPeriod) {
      filters.start_date = range[0].format('YYYY-MM-DD');
      filters.end_date = range[1].format('YYYY-MM-DD');
    } else {
      filters.start_date = range[0].startOf('day').format();
      filters.end_date = range[1].endOf('day').format();
    }
  }
  return filters;
}

function buildDateRangeFromUrl(searchParams, drilldown) {
  if (drilldown.dateRange) return drilldown.dateRange;
  return parseDateRangeFromParams(searchParams);
}

export default function IFAListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const drilldown = useDashboardDrilldownFilters();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canAssignPricingGroup = isSuperAdmin || hasPermission(PERMISSIONS.PARTNER_ASSIGN_PRICING_GROUP);
  const canAssignRm = isSuperAdmin || hasPermission(PERMISSIONS.RBAC_HIERARCHY_EDIT);
  const canChangePartnerStatus = isSuperAdmin || hasPermission(PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT);

  // Table State
  const [ifas, setIfas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pricing group drawer state
  const [pricingDrawerOpen, setPricingDrawerOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [rmDrawerOpen, setRmDrawerOpen] = useState(false);
  const [selectedPartnerForRm, setSelectedPartnerForRm] = useState(null);

  const [inviteOverlayOpen, setInviteOverlayOpen] = useState(false);
  const [inviteOverlayPhase, setInviteOverlayPhase] = useState('loading');
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [statusChangeSubmitting, setStatusChangeSubmitting] = useState(false);
  const [statusChangePartner, setStatusChangePartner] = useState(null);
  const [statusChangeChecklist, setStatusChangeChecklist] = useState(null);

  // Filters State — seeded from URL on first render
  const [filters, setFilters] = useState(() => buildFiltersFromUrl(searchParams, drilldown));
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [dateRange, setDateRange] = useState(() => buildDateRangeFromUrl(searchParams, drilldown));
  const [rmList, setRmList] = useState([]);
  const [sortState, setSortState] = useState(() => ({
    sort_by: searchParams.get('sort_by') || DEFAULT_SORT.sort_by,
    sort_order: searchParams.get('sort_order') || DEFAULT_SORT.sort_order,
  }));
  const [pagination, setPagination] = useState(() => {
    const page = Number(searchParams.get('page'));
    const limit = Number(searchParams.get('limit'));
    return {
      current: page > 0 ? page : 1,
      pageSize: limit > 0 ? limit : DEFAULT_PAGE_SIZE,
      total: 0,
    };
  });

  const fetchIFAs = useCallback(
    async (paramsObj = {}) => {
      setLoading(true);
      try {
        const page = paramsObj.page || pagination.current;
        const limit = paramsObj.pageSize || pagination.pageSize;
        const currentFilters = paramsObj.currentFilters || filters;
        const currentSort = paramsObj.sort || sortState;

        const apiParams = {
          page,
          limit,
          search: currentFilters.search,
          status: currentFilters.status,
          agreement_status: currentFilters.agreement_status,
          sort_by: currentSort.sort_by,
          sort_order: currentSort.sort_order,
        };
        if (currentFilters.manager_id) apiParams.manager_id = currentFilters.manager_id;
        if (currentFilters.start_date) apiParams.start_date = currentFilters.start_date;
        if (currentFilters.end_date) apiParams.end_date = currentFilters.end_date;
        if (currentFilters.active_in_period) apiParams.active_in_period = true;
        if (currentFilters.bank_approval_status) {
          apiParams.bank_approval_status = currentFilters.bank_approval_status;
        }

        const result = await ifaService.getAdminIfas(apiParams);

        if (result && result.users) {
          setIfas(result.users);
          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: limit,
            total: result.pagination?.total || result.pagination?.total_records || result.users.length,
          }));
        } else {
          setIfas([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
        }
      } catch (err) {
        console.error('Failed to fetch partners:', err);
        setIfas([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Stable reference that always calls the latest fetchIFAs without being
  // listed as a dep of useMemo(columns). This prevents columns from being
  // recreated every time a filter changes, which was causing Ant Design to
  // accumulate `ant-table-column-has-sorters` on every re-render.
  const fetchIFAsRef = React.useRef(fetchIFAs);
  useEffect(() => { fetchIFAsRef.current = fetchIFAs; }, [fetchIFAs]);
  const stableFetchIFAs = useCallback(() => fetchIFAsRef.current(), []);

  const filtersRef = React.useRef(filters);
  const sortStateRef = React.useRef(sortState);
  const paginationRef = React.useRef(pagination);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortStateRef.current = sortState; }, [sortState]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  useEffect(() => {
    let cancelled = false;
    orderService
      .listOrderRms()
      .then((rms) => {
        if (!cancelled) setRmList(Array.isArray(rms) ? rms : []);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error(e);
          setRmList([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchIFAs({
      page: pagination.current,
      pageSize: pagination.pageSize,
      currentFilters: filters,
      sort: sortState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filter / sort / pagination state → URL (replace, no history spam)
  useEffect(() => {
    const p = {};
    if (filters.search) p.q = filters.search;
    if (filters.status && filters.status !== 'all') p.status = filters.status;
    if (filters.manager_id) p.rm = filters.manager_id;
    if (filters.agreement_status) p.agreement = filters.agreement_status;
    if (filters.bank_approval_status) p.bank_approval_status = filters.bank_approval_status;

    const isDrilldownDateRange =
      drilldown.metric &&
      drilldown.dateRange?.[0] &&
      drilldown.dateRange?.[1] &&
      dateRange?.[0]?.isSame(drilldown.dateRange[0], 'day') &&
      dateRange?.[1]?.isSame(drilldown.dateRange[1], 'day');

    if (isDrilldownDateRange) {
      p.metric = drilldown.metric;
      p.start_date = dateRange[0].format('YYYY-MM-DD');
      p.end_date = dateRange[1].format('YYYY-MM-DD');
    } else if (dateRange?.[0] && dateRange?.[1]) {
      p.date_from = dateRange[0].format('YYYY-MM-DD');
      p.date_to = dateRange[1].format('YYYY-MM-DD');
      if (filters.active_in_period) p.active_in_period = '1';
    }

    if (pagination.current > 1) p.page = String(pagination.current);
    if (pagination.pageSize !== DEFAULT_PAGE_SIZE) p.limit = String(pagination.pageSize);
    if (sortState.sort_by !== DEFAULT_SORT.sort_by) p.sort_by = sortState.sort_by;
    if (sortState.sort_order !== DEFAULT_SORT.sort_order) p.sort_order = sortState.sort_order;

    setSearchParams(p, { replace: true });
  }, [
    filters,
    dateRange,
    pagination.current,
    pagination.pageSize,
    sortState,
    drilldown.metric,
    drilldown.dateRange,
    setSearchParams,
  ]);

  // Setup debounce for searching
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        const newFilters = { ...filters, search: searchInput };
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchIFAs({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
      }
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filters.search, pagination.pageSize]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchIFAs({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    const newFilters = {
      ...filters,
      start_date: dates
        ? filters.active_in_period
          ? dates[0].format('YYYY-MM-DD')
          : dates[0].startOf('day').format()
        : '',
      end_date: dates
        ? filters.active_in_period
          ? dates[1].format('YYYY-MM-DD')
          : dates[1].endOf('day').format()
        : '',
    };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchIFAs({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange(null);
    const clearedFilters = {
      search: '',
      status: 'all',
      start_date: '',
      end_date: '',
      manager_id: '',
      agreement_status: '',
      bank_approval_status: '',
      active_in_period: false,
    };
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchIFAs({ page: 1, pageSize: pagination.pageSize, currentFilters: clearedFilters, sort: sortState });
  };

  const handleTableChange = useCallback((newPagination, _, sorter) => {
    const currentSort = sortStateRef.current;
    const currentPagination = paginationRef.current;

    const newSort =
      sorter?.field && sorter?.order
        ? { sort_by: sorter.field, sort_order: sorter.order === 'ascend' ? 'asc' : 'desc' }
        : currentSort;

    const pageChanged =
      newPagination.current !== currentPagination.current ||
      newPagination.pageSize !== currentPagination.pageSize;
    const sortChanged =
      newSort.sort_by !== currentSort.sort_by || newSort.sort_order !== currentSort.sort_order;

    if (!pageChanged && !sortChanged) return;

    if (sortChanged) setSortState(newSort);
    fetchIFAsRef.current({
      page: sortChanged ? 1 : newPagination.current,
      pageSize: newPagination.pageSize,
      currentFilters: filtersRef.current,
      sort: newSort,
    });
  }, []);

  const handleResendPasswordSetup = useCallback(async (record) => {
    const partnerId = record?.id ?? record?.uuid;
    if (!partnerId) {
      message.error('Partner ID is missing.');
      return;
    }

    setInviteOverlayPhase('loading');
    setInviteOverlayOpen(true);

    try {
      const response = await ifaService.resendInvite(partnerId);
      if (response && typeof response === 'object' && response.success === false) {
        throw new Error(response.message || response.error || 'Failed to send invite link.');
      }
      setInviteOverlayPhase('success');
    } catch (err) {
      setInviteOverlayOpen(false);
      message.error(err?.response?.data?.message || err?.message || 'Failed to send invite link.');
    }
  }, []);

  const handleCopyRaReferralLink = useCallback(async (record) => {
    const partnerUuid = record?.id ?? record?.uuid;
    const referralLink = buildRaReferralLink(partnerUuid);
    if (!referralLink) {
      message.error('Unable to build RA referral link — partner UUID is missing.');
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      message.success('RA referral link copied');
    } catch {
      message.error('Could not copy link to clipboard.');
    }
  }, []);

  const closeInviteOverlay = useCallback(() => {
    setInviteOverlayOpen(false);
    setInviteOverlayPhase('loading');
  }, []);

  const goToPartnerDetails = useCallback(
    (record) => {
      const partnerUuid = record?.id ?? record?.uuid;
      if (!partnerUuid) return;
      navigate(`/partner/details/${encodeURIComponent(String(partnerUuid))}`);
    },
    [navigate]
  );

  const handleAssignRm = useCallback((record) => {
    setSelectedPartnerForRm(record);
    setRmDrawerOpen(true);
  }, []);

  const handleAssignPricingGroup = useCallback((record) => {
    setSelectedPartner(record);
    setPricingDrawerOpen(true);
  }, []);

  const closeStatusChangeModal = useCallback(() => {
    if (statusChangeSubmitting) return;
    setStatusChangeOpen(false);
    setStatusChangePartner(null);
    setStatusChangeChecklist(null);
    setStatusChangeLoading(false);
  }, [statusChangeSubmitting]);

  const handleChangeStatus = useCallback(async (record) => {
    const partnerId = getPartnerUuid(record);
    if (!partnerId) {
      message.error('Partner ID is missing.');
      return;
    }

    setStatusChangePartner(record);
    setStatusChangeChecklist(null);
    setStatusChangeOpen(true);
    setStatusChangeLoading(true);
    try {
      const response = await ifaService.getStatusChangeChecklist(partnerId);
      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to load status checklist.');
      }
      setStatusChangeChecklist(response?.data ?? response);
    } catch (err) {
      setStatusChangeOpen(false);
      setStatusChangePartner(null);
      message.error(err?.response?.data?.message || err?.message || 'Failed to load status checklist.');
    } finally {
      setStatusChangeLoading(false);
    }
  }, []);

  const handleConfirmStatusChange = useCallback(async () => {
    const partnerId = getPartnerUuid(statusChangePartner);
    if (!partnerId) return;

    setStatusChangeSubmitting(true);
    try {
      const response = await ifaService.changePartnerStatus(partnerId);
      if (response?.success === false) {
        const pendingLabels = (response?.data?.pending_steps || [])
          .map((step) => step.label)
          .filter(Boolean);
        throw new Error(pendingLabels.join(' · ') || response?.message || 'Cannot change status.');
      }
      if (response?.data?.already_approved) {
        message.info(response?.message || ALREADY_APPROVED_COPY);
      } else {
        message.success(response?.message || 'Partner onboarding status changed to approved.');
      }
      setStatusChangeOpen(false);
      setStatusChangePartner(null);
      setStatusChangeChecklist(null);
      fetchIFAsRef.current({
        page: pagination.current,
        pageSize: pagination.pageSize,
        currentFilters: filters,
        sort: sortState,
      });
    } catch (err) {
      const body = err?.response?.data;
      const pendingLabels = (body?.data?.pending_steps || [])
        .map((step) => step.label)
        .filter(Boolean);
      message.error(pendingLabels.join(' · ') || body?.message || err?.message || 'Failed to change status.');
    } finally {
      setStatusChangeSubmitting(false);
    }
  }, [filters, pagination.current, pagination.pageSize, sortState, statusChangePartner]);

  const columns = useMemo(
    () =>
      getPartnerListColumns({
        sortState,
        canAssignRm,
        canAssignPricingGroup,
        canChangePartnerStatus,
        goToPartnerDetails,
        handleResendPasswordSetup,
        handleCopyRaReferralLink,
        onAssignRm: handleAssignRm,
        onAssignPricingGroup: handleAssignPricingGroup,
        onChangeStatus: handleChangeStatus,
      }),
    [
      sortState,
      canAssignRm,
      canAssignPricingGroup,
      canChangePartnerStatus,
      goToPartnerDetails,
      handleResendPasswordSetup,
      handleCopyRaReferralLink,
      handleAssignRm,
      handleAssignPricingGroup,
      handleChangeStatus,
    ]
  );

  const isDashboardDrilldown = Boolean(drilldown.bannerLabel);

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and onboard partners.</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <RmReferralLinkButton />
          <PermissionGate permission={PERMISSIONS.PARTNER_CREATE}>
            <button
              type="button"
              onClick={() => navigate('/partners/onboard')}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 active:scale-[0.99] transition-all shadow-sm"
            >
              <FiPlus size={16} /> Onboard New Partner
            </button>
          </PermissionGate>
        </div>
      </div>


      <div className={isDashboardDrilldown ? undefined : 'space-y-4'}>
        {isDashboardDrilldown ? (
          <Alert
            type="info"
            message={drilldown.bannerLabel}
            showIcon
            className="mb-0! rounded-t-2xl rounded-bl-[0!important] rounded-br-[0!important]"
            action={
              <button
                type="button"
                onClick={handleClearFilters}
                aria-label="Clear filters"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-700/70 transition-colors hover:bg-blue-100/50 hover:text-blue-800"
              >
                <FiX size={18} />
              </button>
            }
          />
        ) : null}

        <div
          className={`bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col ${
            isDashboardDrilldown ? 'rounded-b-2xl rounded-t-none border-t-0' : 'rounded-2xl'
          }`}
        >
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col xl:flex-row xl:items-start gap-4 bg-gray-50/50">
          
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <AppRangePicker
              value={dateRange}
              onChange={handleDateChange}
              className="w-full sm:w-auto h-[38px] rounded-lg"
              placeholder={['Start Date', 'End Date']}
            />
            
            <Select
              showSearch
              placeholder="Filter by RM..."
              value={filters.manager_id || undefined}
              onChange={(val) => handleFilterChange('manager_id', val || '')}
              allowClear
              className="w-full sm:w-[220px] h-[38px]"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {rmList.map((rm) => (
                <Option key={rm.id} value={rm.id}>
                  {rm.name}
                </Option>
              ))}
            </Select>

            <div className="relative w-full sm:w-[320px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, mobile or PAN..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all bg-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2 xl:mt-0">
            <div className="flex items-center gap-2 border-r border-gray-200 pr-3">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Bank review:</span>
              <Select
                value={filters.bank_approval_status || 'all'}
                onChange={(val) => handleFilterChange('bank_approval_status', val === 'all' ? '' : val)}
                className="w-[150px] h-[38px]"
              >
                <Option value="all">All</Option>
                <Option value="pending_review">Pending review</Option>
              </Select>
            </div>
            <div className="flex items-center gap-2 border-r border-gray-200 pr-3">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Agreement:</span>
              <Select
                value={filters.agreement_status || 'all'}
                onChange={(val) => handleFilterChange('agreement_status', val === 'all' ? '' : val)}
                className="w-[120px] h-[38px]"
              >
                <Option value="all">All</Option>
                <Option value="COMPLETED">Completed</Option>
                <Option value="REQUESTED">Requested</Option>
              </Select>
            </div>
            <div className="flex items-center gap-2 border-r border-gray-200 pr-3">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Status:</span>
              <Select
                value={filters.status}
                onChange={(val) => handleFilterChange('status', val)}
                className="w-[100px] h-[38px]"
              >
                <Option value="all">All</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
            <Button onClick={handleClearFilters} className="h-[38px] border-gray-300 text-gray-600 text-sm hover:bg-gray-100">
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={ifas}
            rowKey={(r) => r.id || r.uuid}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '30', '50'],
              className: 'px-4 mb-4 mt-4',
            }}
            onChange={handleTableChange}
            className={`custom-table min-w-[1200px] [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap${
              isDashboardDrilldown ? ' [&_.ant-table-container]:rounded-t-none [&_.ant-table]:rounded-t-none' : ''
            }`}
            rowClassName="hover:bg-gray-50 transition-colors"
          />
        </div>
      </div>
      </div>

      <AssignPricingGroupDrawer
        open={pricingDrawerOpen}
        partner={selectedPartner}
        onClose={() => {
          setPricingDrawerOpen(false);
          setSelectedPartner(null);
        }}
        onSuccess={() => {
          fetchIFAs({
            page: pagination.current,
            pageSize: pagination.pageSize,
            currentFilters: filters,
            sort: sortState,
          });
        }}
      />

      <AssignRmDrawer
        open={rmDrawerOpen}
        partner={selectedPartnerForRm}
        onClose={() => {
          setRmDrawerOpen(false);
          setSelectedPartnerForRm(null);
        }}
        onSuccess={() => {
          fetchIFAs({
            page: pagination.current,
            pageSize: pagination.pageSize,
            currentFilters: filters,
            sort: sortState,
          });
        }}
      />

      <PartnerInviteOverlay
        open={inviteOverlayOpen}
        phase={inviteOverlayPhase}
        onDismiss={closeInviteOverlay}
      />

      <Modal
        title="Change status"
        open={statusChangeOpen}
        onCancel={closeStatusChangeModal}
        destroyOnClose
        footer={
          statusChangeLoading
            ? null
            : statusChangeChecklist?.already_approved || statusChangeChecklist?.ready === false
              ? [
                  <Button key="close" onClick={closeStatusChangeModal}>
                    Close
                  </Button>,
                ]
              : [
                  <Button key="cancel" onClick={closeStatusChangeModal} disabled={statusChangeSubmitting}>
                    Cancel
                  </Button>,
                  <Button
                    key="confirm"
                    type="primary"
                    loading={statusChangeSubmitting}
                    onClick={handleConfirmStatusChange}
                  >
                    Change status
                  </Button>,
                ]
        }
      >
        {statusChangeLoading ? (
          <p className="text-sm text-gray-600">Checking onboarding review checklist…</p>
        ) : statusChangeChecklist?.already_approved ? (
          <p className="text-sm text-gray-700">{ALREADY_APPROVED_COPY}</p>
        ) : statusChangeChecklist?.ready === false ? (
          <div>
            <p className="text-sm text-gray-700 mb-2">Cannot change status yet. Pending:</p>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              {(statusChangeChecklist.pending_steps || []).map((step) => (
                <li key={step.key || step.label}>{step.label}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-700">
            Approve this partner&apos;s onboarding? KYC status will move to Completed after this succeeds.
          </p>
        )}
      </Modal>
    </DashboardPageWrapper>
  );
}
